/**
 * DealerAssignments — T-005 (2026-09-09).
 *
 * Point 4: an OPTIONAL product category on a dealer→manager pairing.
 * Point 5: the SAME dealer under two managers, allowed only when the category
 * differs. A second manager on a category somebody already holds is refused,
 * by name, by the server.
 *
 * 🔒 A PAIRING CARRIES NO PERMISSION (spec §5, option B — LOCKED).
 * `Dealer.managerUserId` remains the single lead-owning manager and is what
 * gates who may see a dealer's leads. Nothing in this file reads, widens or
 * consults access, and nothing here may start to.
 *
 * 🔒 THE SERVER OWNS "WHO MAY BE ASSIGNED". This component renders exactly what
 * `GET /dealers/assignable-managers` returns and never filters by role itself.
 * The role set lives in `dealer-access.ts` (DEALER_MANAGER_ROLES) and differs
 * from the sales-orders MANAGER_ROLES set — a client-side copy would drift the
 * moment either changes.
 *
 * 🔒 THE REFUSAL MESSAGE IS THE SERVER'S, VERBATIM. A 409 names the manager who
 * already holds the category. Collapsing that into "Failed to assign" throws
 * away the only part of the refusal a user can act on.
 */
import { useCallback, useEffect, useState } from 'react';
import { UserCog, X, Plus, AlertTriangle } from 'lucide-react';
import { api } from '@/utils/api';

/**
 * The eight real categories, in the server's own order.
 * `dealer-product-categories.ts` is the authority; this is the display copy and
 * the two must not diverge. An EXACT match is required server-side — a casing
 * variant is refused rather than normalised — so these strings are sent as-is.
 */
export const DEALER_PRODUCT_CATEGORIES = [
  'SS_TANKS',
  'SS_PLUMBING',
  'SWH',
  'DOMESTIC_HP',
  'COMMERCIAL_HP',
  'SOLAR_SPACE_HEATING',
  'COMMERCIAL_HWT',
  'HEADERS',
] as const;

/** Same wording as the lead-router page, so one product reads one way. */
const CATEGORY_LABELS: Record<string, string> = {
  SS_TANKS: 'SS Tanks',
  SS_PLUMBING: 'SS Plumbing',
  SWH: 'SWH',
  DOMESTIC_HP: 'Domestic HP',
  COMMERCIAL_HP: 'Commercial HP',
  SOLAR_SPACE_HEATING: 'Solar Space Heating',
  COMMERCIAL_HWT: 'Commercial HWT',
  HEADERS: 'Headers',
};

/** Never hide a pairing whose category we do not recognise — show the key. */
export const categoryLabel = (key: string | null): string =>
  key == null ? 'No category' : (CATEGORY_LABELS[key] ?? key);

export interface Pairing {
  id: string;
  managerUserId: string;
  managerName: string;
  category: string | null;
}

export interface AssignableManager {
  id: string;
  name: string;
  role: string | null;
}

/** The server's error body, when it sent one. */
function serverMessage(err: unknown): string | null {
  const res = (err as { response?: { data?: { error?: unknown } } })?.response;
  const msg = res?.data?.error;
  return typeof msg === 'string' && msg.trim() ? msg : null;
}

export function DealerAssignments({
  dealerId,
  canEdit,
  onChanged,
}: {
  dealerId: string;
  canEdit: boolean;
  /** Lets the parent refresh a Manager column that shows these pairings. */
  onChanged?: () => void;
}) {
  const [pairings, setPairings] = useState<Pairing[] | null>(null);
  const [assignable, setAssignable] = useState<AssignableManager[]>([]);
  const [managerUserId, setManagerUserId] = useState('');
  const [category, setCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadPairings = useCallback(async () => {
    try {
      const res = await api.get(`/dealers/${dealerId}/assignments`);
      setPairings((res.data?.data ?? []) as Pairing[]);
      setLoadError(null);
    } catch (err) {
      // A failed load must not render as "no pairings" — an empty list and an
      // unreachable server look identical on screen and mean opposite things.
      setPairings(null);
      setLoadError(serverMessage(err) ?? 'Could not load the pairings for this dealer.');
    }
  }, [dealerId]);

  useEffect(() => { void loadPairings(); }, [loadPairings]);

  useEffect(() => {
    if (!canEdit) return;
    let live = true;
    api.get('/dealers/assignable-managers')
      .then((res) => { if (live) setAssignable((res.data?.data ?? []) as AssignableManager[]); })
      .catch(() => { if (live) setAssignable([]); });
    return () => { live = false; };
  }, [canEdit]);

  const assign = async () => {
    if (!managerUserId || busy) return;
    setBusy(true);
    setError(null);
    try {
      // `category: null` is a real value, not an omission — "no category" is
      // one slot on this dealer, and the server enforces exactly one holder.
      await api.post(`/dealers/${dealerId}/assignments`, {
        managerUserId,
        category: category || null,
      });
      setManagerUserId('');
      setCategory('');
      await loadPairings();
      onChanged?.();
    } catch (err) {
      setError(serverMessage(err) ?? 'Could not add that pairing.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (assignmentId: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/dealers/${dealerId}/assignments/${assignmentId}`);
      await loadPairings();
      onChanged?.();
    } catch (err) {
      setError(serverMessage(err) ?? 'Could not remove that pairing.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-[var(--line)] bg-white p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-[var(--ink-0)]">Managers by product category</h3>
        <span className="text-[11px] text-[var(--ink-3)]">
          Two managers may share a dealer when the category differs
        </span>
      </div>

      {loadError && (
        <p className="mb-3 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {loadError}
        </p>
      )}

      {pairings !== null && pairings.length === 0 && !loadError && (
        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
          No manager paired for any category yet.
        </p>
      )}

      {pairings !== null && pairings.length > 0 && (
        <ul data-testid="dealer-pairings" className="mb-3 divide-y divide-[var(--line)]">
          {pairings.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 py-2">
              <span className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 text-sm text-[var(--ink-1)]">
                  <UserCog className="h-3.5 w-3.5 text-[var(--ink-3)]" /> {p.managerName}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    p.category
                      ? 'bg-sky-100 text-sky-800'
                      : 'bg-[var(--line)] text-[var(--ink-3)]'
                  }`}
                >
                  {categoryLabel(p.category)}
                </span>
              </span>
              {canEdit && (
                <button
                  type="button"
                  aria-label={`Remove pairing with ${p.managerName}`}
                  onClick={() => void remove(p.id)}
                  disabled={busy}
                  className="rounded p-1 text-[var(--ink-3)] hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex min-w-[190px] flex-1 flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-3)]">
              Assign to
            </span>
            <select
              value={managerUserId}
              onChange={(e) => setManagerUserId(e.target.value)}
              className="rounded-lg border border-[var(--line)] px-2 py-1.5 text-sm"
            >
              <option value="">Select a manager…</option>
              {/* Rendered exactly as the server sent it — no role filter here. */}
              {assignable.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.role ? `${m.name} (${m.role})` : m.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex min-w-[190px] flex-1 flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-3)]">
              Product category
            </span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="rounded-lg border border-[var(--line)] px-2 py-1.5 text-sm"
            >
              {/* Optional means optional — this is a real choice, not a prompt. */}
              <option value="">No category</option>
              {DEALER_PRODUCT_CATEGORIES.map((k) => (
                <option key={k} value={k}>{categoryLabel(k)}</option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() => void assign()}
            disabled={!managerUserId || busy}
            className="inline-flex items-center gap-1 rounded-lg bg-[var(--forest)] px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" /> Assign
          </button>
        </div>
      )}

      {error && (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          {/* The server's own words. A 409 names the manager who holds it. */}
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {error}
        </p>
      )}
    </section>
  );
}
